import React from "react";
import { CheckCircle, XCircle, ExternalLink } from "lucide-react";

import { TestRunStatus } from "./useWorkflowTestRunWebSocket";

import { ProviderApiKeyPanel } from "@/components/ProviderApiKeyPrompt";
import { CopyButton } from "@/components/ui/copy-button";
import LoadingSpinner from "@/components/LoadingSpinner";

interface TestRunStatusDisplayProps {
  status: TestRunStatus | "idle";
  message: string;
  traceId: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any;
  error: {
    code: string;
    message: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details?: Record<string, any>;
  } | null;
  progress?: number;
  summary?: {
    total_nodes?: number;
    slowest_node?: { id: string; duration_ms: number };
    token_usage?: {
      prompt: number;
      completion: number;
      total: number;
      cost_usd?: number;
    };
  };
  duration?: number;
  workspaceId: string;
  providerApiKeyPrompt?: {
    provider: string;
    onAfterProviderKeySaved: () => void;
  } | null;
}

export const TestRunStatusDisplay: React.FC<TestRunStatusDisplayProps> = ({
  status,
  message,
  traceId,
  result,
  error,
  progress,
  summary,
  duration,
  workspaceId,
  providerApiKeyPrompt,
}) => (
  <>
    {/* Queued State */}
    {status === TestRunStatus.QUEUED && (
      <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center">
          <LoadingSpinner size={5} className="mr-3 text-gray-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-800">{message}</p>
          </div>
        </div>
      </div>
    )}

    {/* Running State */}
    {status === TestRunStatus.RUNNING && (
      <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-center">
          <LoadingSpinner size={5} className="mr-3 text-blue-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">{message}</p>
            {progress !== undefined && (
              <div className="mt-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-blue-200">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-blue-600">{progress.toFixed(0)}% complete</p>
              </div>
            )}
            {traceId && (
              <div className="mt-2">
                <a
                  href={`/workspace/${workspaceId}/traces/${traceId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-xs text-blue-700 underline hover:text-blue-800"
                >
                  View live trace
                  <ExternalLink className="ml-1 h-3 w-3" />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Success State */}
    {status === TestRunStatus.SUCCESS && (
      <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-4">
        <div>
          <p className="flex items-center text-sm font-medium text-green-800">
            <CheckCircle className="mr-2 h-4 w-4 text-green-400" />
            {message}
            {duration && (
              <span className="ml-2 text-xs text-green-600">({(duration / 1000).toFixed(2)}s)</span>
            )}
          </p>
          {result && (
            <div className="mt-3">
              <div className="relative max-h-32 overflow-y-auto rounded border bg-gray-50 p-3">
                <pre className="whitespace-pre-wrap break-words pr-8 font-mono text-xs text-gray-900">
                  {typeof result === "string" ? result : JSON.stringify(result, null, 2)}
                </pre>
                <div className="absolute right-2 top-2">
                  <CopyButton
                    text={typeof result === "string" ? result : JSON.stringify(result, null, 2)}
                    iconSize="h-4 w-4"
                    title="Copy result"
                  />
                </div>
              </div>
            </div>
          )}
          {summary?.token_usage && (
            <div className="mt-2 text-xs text-green-700">
              Tokens: {summary.token_usage.total}
              {summary.token_usage.cost_usd && (
                <span> (${summary.token_usage.cost_usd.toFixed(4)})</span>
              )}
            </div>
          )}
          {traceId && (
            <div className="mt-2">
              <a
                href={`/workspace/${workspaceId}/traces/${traceId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs text-green-700 underline hover:text-green-800"
              >
                View execution trace
                <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Failed State */}
    {status === TestRunStatus.FAILED && (
      <div className="mb-4">
        {providerApiKeyPrompt ? (
          <ProviderApiKeyPanel
            provider={providerApiKeyPrompt.provider}
            onAfterProviderKeySaved={providerApiKeyPrompt.onAfterProviderKeySaved}
          />
        ) : (
          <div className="rounded-md border border-red-200 bg-red-50 p-4">
            <div>
              <p className="flex items-center text-sm font-medium text-red-800">
                <XCircle className="mr-2 h-4 w-4 text-red-400" />
                {message}
              </p>
              {error && (
                <div className="mt-2">
                  <p className="text-sm text-red-700">
                    <span className="font-medium">Error:</span> {error.message}
                  </p>
                  {error.code && <p className="mt-1 text-xs text-red-600">Code: {error.code}</p>}
                  {error.details && (
                    <div className="mt-2 rounded-md bg-white p-2">
                      <pre className="text-xs text-red-600">
                        {JSON.stringify(error.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
              {traceId && (
                <div className="mt-2">
                  <a
                    href={`/workspace/${workspaceId}/traces/${traceId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-xs text-red-700 underline hover:text-red-800"
                  >
                    View execution trace
                    <ExternalLink className="ml-1 h-3 w-3" />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )}
  </>
);
